import { useMemo } from 'react';

import type { Crm } from 'domain/crm';
import {
  createQuotaOperationInfo,
  type DataCreditsStateDto,
  EMPTY_QUOTA,
  type Quota,
  type QuotaOperationInfo,
} from 'domain/quota';
import {
  calculateRevealCost,
  getCrmExportPrice,
  getExportPriceLegacy,
  getRevealPriceLegacy,
} from 'domain/quota/operations';
import { useUserQuery } from 'features/user/useUserQuery';

import { useAppState } from '../../../AppStateProvider';
import { CompanyTabName, Page } from '../../../domain/page';
import { useDataCreditsQuery } from '../useDataCreditsQuery';
import { useExportQuotaQuery } from '../useExportQuotaQuery';

import { useIsDataCreditsEnabled } from './useIsDataCreditsEnabled';

const EMPTY_DATA_CREDITS_DTO: DataCreditsStateDto = {
  usageState: EMPTY_QUOTA,
  dataPoints: {
    company: { salesforce: 1, hubspot: 1 },
    contact: 1,
  },
};

type QuotaState = {
  isLoading: boolean;

  // Unified quota
  quota: QuotaOperationInfo;

  // Computed helpers
  canAfford: (cost: number) => boolean;
  getRevealCost: (count: number) => number;
  getExportCost: (crm: Crm) => number;
};

export const useQuota = (): QuotaState => {
  const { companyTabName, page } = useAppState();
  const { isDataCreditsEnabled } = useIsDataCreditsEnabled();

  // Queries for users with packages >= v4
  const dataCreditsQuery = useDataCreditsQuery();

  // Legacy queries
  const userQuery = useUserQuery();
  const exportQuotaQuery = useExportQuotaQuery(!isDataCreditsEnabled);

  return useMemo(() => {
    if (isDataCreditsEnabled) {
      const { usageState, dataPoints } = dataCreditsQuery.data ?? EMPTY_DATA_CREDITS_DTO;
      const quotaInfo = createQuotaOperationInfo(usageState);

      return {
        isLoading: dataCreditsQuery.isLoading,

        quota: quotaInfo,
        canAfford: (cost: number) => usageState.remaining >= cost,
        getRevealCost: (count: number) => calculateRevealCost(count, dataPoints.contact),
        getExportCost: (crm: Crm) => getCrmExportPrice(dataPoints, crm),
      };
    }

    // Legacy mode - use user.contactsQuota and exportQuota
    const showExportQuota = companyTabName === CompanyTabName.Overview;
    const showContactsQuota = page === Page.Settings || companyTabName === CompanyTabName.Contacts;

    const quotaByTab: Quota | undefined | null = showContactsQuota
      ? userQuery.data?.contactsQuota
      : showExportQuota
        ? exportQuotaQuery.data
        : null;
    const legacyQuotaState: Quota = quotaByTab ?? EMPTY_QUOTA;

    // In legacy mode, isLow should always be false
    const quotaInfo = createQuotaOperationInfo(legacyQuotaState, { isLow: false });

    return {
      isLoading: userQuery.isLoading || exportQuotaQuery.isLoading,

      quota: quotaInfo,

      canAfford: (cost: number) => legacyQuotaState.remaining >= cost,
      getRevealCost: (count: number) => getRevealPriceLegacy(count),
      getExportCost: (_crm: Crm) => getExportPriceLegacy(),
    };
  }, [page, companyTabName, exportQuotaQuery, isDataCreditsEnabled, dataCreditsQuery, userQuery]);
};
